import {
  useGetFolders,
  type TFolderWithChildren,
} from "@pages/analytics/hooks";
import { useAnalyticsStoreShallow } from "@pages/analytics/store";
import { filterFolderTreeByName } from "@pages/analytics/utils";
import { IconFolder, IconFolderSymlink } from "@tabler/icons-react";
import { useCallback, useMemo } from "react";

const FolderItem = ({
  folder,
  depth = 0,
  onFolderChange,
}: {
  folder: TFolderWithChildren;
  depth?: number;
  onFolderChange: (folder: TFolderWithChildren) => void;
}) => {
  return (
    <>
      <button
        type="button"
        className="tw-py-1.5 tw-w-full tw-px-2 tw-outline-none tw-text-blue tw-grid tw-grid-cols-[auto_1fr] tw-text-sm tw-transition-colors hover:tw-bg-blue-50 tw-cursor-pointer tw-gap-2 tw-items-center tw-justify-start tw-rounded"
        // Search results include a match's ancestors, so the rows only read as
        // a path if nesting is visible. Depth is unbounded, hence the computed
        // indent rather than a class; depth 0 keeps the tw-px-2 inset.
        style={{ paddingLeft: `${0.5 + depth * 0.75}rem` }}
        onClick={() => onFolderChange(folder)}
      >
        {folder.parentId ? (
          <IconFolderSymlink className="tw-shrink-0" />
        ) : (
          <IconFolder className="tw-shrink-0" />
        )}
        <span className="tw-truncate tw-text-left">{folder.name}</span>
      </button>
      {folder.children.map((childFolder) => (
        <FolderItem
          key={childFolder._id}
          folder={childFolder}
          depth={depth + 1}
          onFolderChange={onFolderChange}
        />
      ))}
    </>
  );
};

const SKELETON_WIDTHS = [65, 80, 45, 90, 55];

const FoldersSkeleton = () => {
  return (
    <>
      {SKELETON_WIDTHS.map((width) => (
        <div
          key={width}
          className="tw-flex tw-h-8 tw-w-full tw-items-center tw-justify-between tw-rounded tw-px-2 tw-animate-skeleton-loading"
        >
          <span
            className="tw-block tw-h-2 tw-w-24 tw-rounded-lg tw-bg-blue-100"
            style={{ width: `${width}%` }}
          />
        </div>
      ))}
    </>
  );
};

export const FoldersGroup = ({
  searchTerm,
  onClose,
}: {
  searchTerm: string;
  onClose: () => void;
}) => {
  const { folders, isLoading } = useGetFolders();
  const { setMultipleFilters } = useAnalyticsStoreShallow((s) => ({
    setMultipleFilters: s.setMultipleFilters,
  }));

  const filteredFolders = useMemo(
    () => filterFolderTreeByName(folders, searchTerm),
    [folders, searchTerm],
  );

  const handleFolderChange = useCallback(
    (folder: TFolderWithChildren) => {
      setMultipleFilters({
        folder: { id: folder._id, name: folder.name },
        location: "",
        story: { id: "", title: "" },
        version: { id: "", title: "" },
        user: { id: "", name: "" },
      });
      onClose();
    },
    [setMultipleFilters, onClose],
  );

  return (
    <div className="tw-p-1 tw-border-b tw-border-b-solid tw-border-b-blue-100">
      <span className="tw-py-1 tw-px-2 tw-block tw-text-blue-500 tw-text-xs tw-font-sans">
        Folders
      </span>
      {isLoading ? (
        <FoldersSkeleton />
      ) : filteredFolders.length === 0 ? (
        <div className="tw-py-2 tw-text-center tw-text-sm tw-text-blue-500">
          {searchTerm ? "No folders found" : "No folders available"}
        </div>
      ) : (
        filteredFolders.map((folder) => (
          <FolderItem
            key={folder._id}
            folder={folder}
            onFolderChange={handleFolderChange}
          />
        ))
      )}
    </div>
  );
};
